import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import biensService from "../services/biensService";
import { withMobilePrompt } from "../hoc/withMobilePrompt";
import {
  dossiersService,
  Dossier,
  DocumentItem,
} from "../services/dossiersService";
import notesService, { Note } from "../services/notesService";
import { Bien } from "../types";
import { colors } from "../theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFolder,
  faFolderPlus,
  faFile,
  faFileAlt,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faNoteSticky,
  faEdit,
  faTrash,
  faTimes,
  faEye,
  faDownload,
  faShare,
  faList,
  faTh,
  faUpload,
  faCloudUploadAlt,
  faHome,
  faBook,
  faHammer,
  faClipboard,
  faRocket,
  faScroll,
  faEllipsisV,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

type ViewMode = "grid" | "list";
type ActiveSection = "historique" | "travaux" | "gestion";

interface ListItem {
  id: string;
  type: "dossier" | "document" | "note";
  nom: string;
  description?: string;
  createdAt: Date;
  dateduDocument?: Date;
  mimeType?: string;
  url?: string;
}

interface BienDetailsProps {
  bien?: Bien;
  onClose?: () => void;
}

const BienDetailsPage: React.FC<BienDetailsProps> = ({
  bien: propBien,
  onClose,
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const [bien, setBien] = useState<Bien | null>(propBien || null);
  const [loading, setLoading] = useState(!propBien);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [activeSection, setActiveSection] =
    useState<ActiveSection>("gestion");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [currentDossierId, setCurrentDossierId] = useState<string | null>(null);

  const [allDossiers, setAllDossiers] = useState<Dossier[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [visibleItems, setVisibleItems] = useState<ListItem[]>([]);

  const [showCreateDossierModal, setShowCreateDossierModal] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentItem | null>(
    null
  );
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [showItemMenu, setShowItemMenu] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);

  const [newDossierName, setNewDossierName] = useState("");
  const [newDossierDescription, setNewDossierDescription] = useState("");
  const [isCreatingDossier, setIsCreatingDossier] = useState(false);

  const [newNoteName, setNewNoteName] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isCreatingNote, setIsCreatingNote] = useState(false);

  // États pour drag & drop
  const [isDragging, setIsDragging] = useState(false);
  const [draggedFiles, setDraggedFiles] = useState<FileList | null>(null);
  const [showDropModal, setShowDropModal] = useState(false);
  const [dropDestination, setDropDestination] = useState<ActiveSection | null>(
    null
  );
  const [dropDossierId, setDropDossierId] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // États pour renommage
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingItem, setRenamingItem] = useState<{
    id: string;
    type: "dossier" | "document";
    currentName: string;
    currentDescription?: string;
  } | null>(null);
  const [newItemName, setNewItemName] = useState("");
  const [newItemDescription, setNewItemDescription] = useState("");

  // État pour sous-dossiers dépliés
  const [expandedDossiers, setExpandedDossiers] = useState<Set<string>>(
    new Set()
  );

  // États pour l'upload avec preview
  const [showUploadPreviewModal, setShowUploadPreviewModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadFileNames, setUploadFileNames] = useState<{
    [key: string]: string;
  }>({});
  const [uploadFileDates, setUploadFileDates] = useState<{
    [key: string]: string;
  }>({});
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [uploadDestination, setUploadDestination] = useState<{
    section: ActiveSection;
    dossierId: string | null;
  } | null>(null);

  // États pour la suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: "dossier" | "document";
    name: string;
  } | null>(null);

  // État pour l'onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  // État pour le menu hamburger mobile
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // États pour la modification d'un document historique
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocumentItem | null>(null);
  const [editDocumentName, setEditDocumentName] = useState("");
  const [editDocumentDate, setEditDocumentDate] = useState("");

  //Allowed ou pas.
  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/jpg",
    "application/pdf",
    "text/plain",
    "text/xml",
    "application/xml",
    "text/csv",
    "application/json",
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

  // Détecter si mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleDossierExpansion = (dossierId: string) => {
    setExpandedDossiers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dossierId)) {
        newSet.delete(dossierId);
      } else {
        newSet.add(dossierId);
      }
      return newSet;
    });
  };

  // Fonction pour obtenir les sous-dossiers d'un dossier
  const getSubDossiers = (parentId: string): Dossier[] => {
    return allDossiers.filter((d) => d.parentId === parentId);
  };

  // Fonction pour obtenir les documents d'un dossier
  const getDocumentsInDossier = (dossierId: string): DocumentItem[] => {
    return documents.filter((doc) => doc.dossierId === dossierId);
  };

  //Fonction pour autoriser ou non certaines extensions
  const isFileAllowed = (file: File) => {
    return (
      (ALLOWED_MIME_TYPES.includes(file.type) ||
        file.type.startsWith("text/") ||
        file.name.endsWith(".xml") ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".txt") ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".ai") ||
        file.name.endsWith(".doc") ||
        file.name.endsWith(".json")) &&
      file.size <= MAX_FILE_SIZE
    );
  };

  const styles = {
    container: {
      minHeight: "100vh",
      position: "relative" as const,
      background: colors.white,
    },
    patternOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: "330px",
      opacity: bien?.image ? 0 : 0.1,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3Ccircle cx='10' cy='30' r='2'/%3E%3Ccircle cx='50' cy='30' r='2'/%3E%3Ccircle cx='30' cy='10' r='2'/%3E%3Ccircle cx='30' cy='50' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      pointerEvents: "none" as const,
      zIndex: 1,
    },
    heroHeader: {
      position: "relative" as const,
      height: "330px",
      background: bien?.image
        ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.65)), url(${bien.image})`
        : `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.65)), url(/home.jpg)`,
      backgroundSize: "cover",
      backgroundPosition: "center center",
      backgroundRepeat: "no-repeat",
      display: "flex",
      alignItems: "flex-end",
      color: "white",
      overflow: "hidden",
    },
    heroOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "transparent",
    },
    heroContent: {
      position: "relative" as const,
      zIndex: 2,
      width: "100%",
      margin: "0 auto",
      //padding: '0 24px 32px',
      padding: "0px 24px 85px",
    },
    heroActions: {
      position: "absolute" as const,
      top: "24px",
      left: "24px",
      right: "24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      zIndex: 3,
    },
    backButton: {
      height: "48px",
      width: "48px",
      borderRadius: "50%",
      backgroundColor: "rgba(255,255,255,0.95)",
      padding: "14px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: colors.text,
      backdropFilter: "blur(12px)",
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    viewToggle: {
      padding: "12px",
      borderRadius: "50%",
      backgroundColor:
        viewMode === "grid" ? colors.primary : "rgba(255,255,255,0.95)",
      border: "none",
      height: "48px",
      width: "48px",
      cursor: "pointer",
      color: viewMode === "grid" ? colors.white : colors.text,
      fontSize: "20px",
      backdropFilter: "blur(12px)",
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    heroTitle: {
      fontSize: "36px",
      fontWeight: "800",
      marginBottom: "12px",
      textShadow: "0 2px 8px rgba(0,0,0,0.4)",
      letterSpacing: "-0.5px",
    },
    heroSubtitle: {
      fontSize: "18px",
      opacity: 0.95,
      marginBottom: "20px",
      textShadow: "0 1px 4px rgba(0,0,0,0.4)",
      fontWeight: "500",
    },
    heroBadge: {
      display: "inline-block",
      backgroundColor: "rgba(255,255,255,0.25)",
      backdropFilter: "blur(12px)",
      padding: "10px 20px",
      borderRadius: "24px",
      fontSize: "14px",
      fontWeight: "700",
      border: "1.5px solid rgba(255,255,255,0.4)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
    navigationHeader: {
      backgroundColor: colors.white,
      boxShadow: "0 -10px 40px rgba(0,0,0,0.1)",
      position: "sticky" as const,
      top: 0,
      marginTop: "-25px",
      zIndex: 100,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      borderRadius: "24px 24px 0 0",
      overflow: "hidden",
    },
    sectionTabs: {
      display: "flex",
      borderBottom: "none",
      backgroundColor: colors.white,
      padding: "0 8px",
    },
    sectionTab: {
      flex: 1,
      padding: "18px 16px",
      backgroundColor: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "700",
      color: colors.gray,
      borderBottomWidth: "3px",
      borderBottomStyle: "solid" as const,
      borderBottomColor: "transparent",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative" as const,
    },
    activeTab: {
      color: colors.primary,
      borderBottomColor: colors.primary,
      backgroundColor: "rgba(139, 69, 19, 0.04)",
    },
    breadcrumb: {
      padding: "14px 24px",
      backgroundColor: "#fafbfc",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "14px",
    },
    breadcrumbButton: {
      background: "none",
      border: "none",
      color: colors.primary,
      cursor: "pointer",
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "600",
      transition: "all 0.2s ease",
    },
    content: {
      padding: "32px 24px 100px",
      margin: "0 auto",
      backgroundColor: colors.white,
      minHeight: "calc(100vh - 280px)",
    },
    sectionDescription: {
      marginBottom: "40px",
      textAlign: "center" as const,
      padding: "0 16px",
    },
    sectionTitle: {
      fontSize: "28px",
      fontWeight: "800",
      color: colors.primary,
      marginBottom: "12px",
      margin: 0,
      letterSpacing: "-0.5px",
    },
    sectionSubtitle: {
      fontSize: "16px",
      color: "#6b7280",
      lineHeight: 1.7,
      maxWidth: "700px",
      margin: "0 auto",
    },
    actionBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "28px",
      gap: "16px",
      flexWrap: "wrap" as const,
    },
    actionButtons: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap" as const,
    },
    actionButton: {
      padding: "12px 20px",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    secondaryButton: {
      backgroundColor: colors.black,
      color: colors.white,
    },
    noteButton: {
      backgroundColor: colors.blue,
      color: colors.white,
    },
    timelineContainer: {
      maxWidth: "900px",
      margin: "0 auto",
    },
    timelineActions: {
      textAlign: "center" as const,
      marginBottom: "40px",
    },
    timeline: {
      position: "relative" as const,
      paddingLeft: "8px",
    },
    timelineItem: {
      display: "flex",
      marginBottom: "32px",
      position: "relative" as const,
      animation: "fadeInUp 0.5s ease-out",
    },
    timelineLine: {
      position: "relative" as const,
      marginRight: "24px",
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center" as const,
    },
    timelineDot: {
      width: "14px",
      height: "14px",
      borderRadius: "50%",
      backgroundColor: colors.primary,
      border: `4px solid ${colors.white}`,
      boxShadow:
        "0 0 0 4px rgba(139, 69, 19, 0.1), 0 4px 12px rgba(0,0,0,0.15)",
      zIndex: 2,
      transition: "all 0.3s ease",
    },
    timelineConnector: {
      width: "3px",
      flex: 1,
      background: `linear-gradient(to bottom, ${colors.primary}, #e5e7eb)`,
      marginTop: "8px",
      borderRadius: "2px",
    },
    timelineContent: {
      flex: 1,
      minWidth: 0,
    },
    timelineCard: {
      backgroundColor: colors.white,
      borderRadius: "16px",
      border: "1px solid rgba(0,0,0,0.06)",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    timelineCardHeader: {
      padding: "20px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    timelineDocIcon: {
      fontSize: "28px",
      flexShrink: 0,
    },
    timelineDocInfo: {
      flex: 1,
      minWidth: 0,
    },
    timelineDocName: {
      fontSize: "17px",
      fontWeight: "700",
      color: colors.black,
      margin: 0,
      marginBottom: "6px",
      letterSpacing: "-0.2px",
    },
    timelineDocDate: {
      fontSize: "14px",
      color: "#6b7280",
      margin: 0,
      fontWeight: "500",
    },
    timelineDocAction: {
      padding: "10px",
      border: "none",
      borderRadius: "10px",
      backgroundColor: "#f3f4f6",
      cursor: "pointer",
      fontSize: "18px",
      transition: "all 0.2s ease",
    },
    timelinePreview: {
      borderTop: "1px solid rgba(0,0,0,0.06)",
      padding: "0",
      overflow: "hidden",
    },
    timelinePreviewImage: {
      width: "100%",
      height: "240px",
      objectFit: "cover" as const,
      cursor: "pointer",
      transition: "transform 0.3s ease",
    },
    itemsGrid: {
      display: "grid",
      gridTemplateColumns:
        viewMode === "grid" ? "repeat(auto-fill, minmax(220px, 1fr))" : "1fr",
      gap: "20px",
    },
    itemCard: {
      backgroundColor: colors.white,
      borderRadius: "16px",
      padding: "20px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "1px solid rgba(0,0,0,0.06)",
    },
    expandButton: {
      position: "absolute" as const,
      bottom: "12px",
      left: "50%",
      transform: "translateX(-50%)",
      padding: "6px 16px",
      border: "none",
      borderRadius: "20px",
      backgroundColor: "rgba(139, 69, 19, 0.1)",
      color: colors.primary,
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "700",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    subItemsContainer: {
      marginTop: "16px",
      paddingTop: "16px",
      borderTop: "2px solid rgba(139, 69, 19, 0.1)",
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    subItem: {
      padding: "10px 12px",
      backgroundColor: "rgba(139, 69, 19, 0.03)",
      borderRadius: "8px",
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      border: "1px solid rgba(139, 69, 19, 0.1)",
    },
    uploadPreviewCard: {
      backgroundColor: colors.white,
      borderRadius: "12px",
      padding: "16px",
      marginBottom: "16px",
      border: "2px solid rgba(0,0,0,0.06)",
      transition: "all 0.3s ease",
    },
    uploadPreviewImage: {
      width: "100%",
      height: "180px",
      objectFit: "cover" as const,
      borderRadius: "8px",
      marginBottom: "12px",
      backgroundColor: "#f3f4f6",
    },
    progressBar: {
      width: "100%",
      height: "8px",
      backgroundColor: "#e5e7eb",
      borderRadius: "8px",
      overflow: "hidden",
      marginTop: "12px",
    },
    progressBarFill: {
      height: "100%",
      background: `linear-gradient(90deg, ${colors.primary}, ${colors.warning})`,
      transition: "width 0.3s ease",
      borderRadius: "8px",
    },
    deleteButton: {
      position: "absolute" as const,
      top: "8px",
      left: "8px",
      padding: "6px",
      border: "none",
      borderRadius: "6px",
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      cursor: "pointer",
      fontSize: "14px",
      opacity: 0.7,
      transition: "all 0.2s ease",
      zIndex: 2,
    },
    onboardingDot: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: "#d1d5db",
      transition: "all 0.3s ease",
      cursor: "pointer",
    },
    onboardingDotActive: {
      backgroundColor: colors.primary,
      width: "32px",
      borderRadius: "5px",
    },
    itemIcon: {
      fontSize: "40px",
      marginBottom: "3px",
      textAlign: "center" as const,
    },
    itemName: {
      fontWeight: "700",
      fontSize: "15px",
      marginBottom: "6px",
      wordBreak: "break-word" as const,
      color: colors.text,
      lineHeight: 1.4,
    },
    itemDescription: {
      fontSize: "13px",
      color: "#6b7280",
      lineHeight: 1.5,
      marginTop: "4px",
    },
    emptyState: {
      textAlign: "center" as const,
      padding: "80px 20px",
      color: "#6b7280",
    },
    emptyIcon: {
      fontSize: "72px",
      marginBottom: "20px",
      opacity: 0.6,
    },
    modal: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
      animation: "fadeIn 0.2s ease-out",
    },
    modalContent: {
      backgroundColor: colors.white,
      borderRadius: "20px",
      padding: "32px",
      maxWidth: "540px",
      width: "90%",
      maxHeight: "85vh",
      overflow: "auto",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      animation: "slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    modalHeader: {
      marginBottom: "24px",
      textAlign: "center" as const,
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: "800",
      color: colors.text,
      marginBottom: "8px",
      letterSpacing: "-0.3px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "15px",
      marginBottom: "16px",
      outline: "none",
      boxSizing: "border-box" as const,
      transition: "all 0.2s ease",
      fontWeight: "500",
    },
    textarea: {
      width: "100%",
      padding: "14px 16px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "15px",
      marginBottom: "16px",
      outline: "none",
      resize: "vertical" as const,
      minHeight: "100px",
      fontFamily: "inherit",
      boxSizing: "border-box" as const,
      transition: "all 0.2s ease",
      lineHeight: 1.6,
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "24px",
    },
    button: {
      padding: "14px 28px",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "700",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    cancelButton: {
      backgroundColor: "#e5e7eb",
      color: "#374151",
    },
    saveButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    hiddenInput: {
      display: "none",
    },
    floatingActionBar: {
      position: "fixed" as const,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: colors.white,
      padding: "16px 24px",
      boxShadow: "0 -4px 24px rgba(0,0,0,0.12)",
      borderTop: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      zIndex: 50,
      backdropFilter: "blur(10px)",
      transition: "all 0.3s ease",
    },
    floatingActionBarMobile: {
      padding: "12px 16px",
      overflowX: "auto" as const,
      overflowY: "hidden" as const,
      WebkitOverflowScrolling: "touch" as const,
      scrollbarWidth: "none" as const,
      msOverflowStyle: "none" as const,
    },
    floatingActionBarLeft: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      transition: "all 0.3s ease",
    },
    floatingActionBarLeftMobile: {
      gap: "8px",
      flexWrap: "nowrap" as const,
      overflowX: "auto" as const,
      flex: 1,
      paddingRight: "12px",
      scrollbarWidth: "none" as const,
      WebkitOverflowScrolling: "touch" as const,
    },
    floatingActionBarRight: {
      display: "flex",
      gap: "12px",
      alignItems: "center",
      transition: "all 0.3s ease",
    },
    floatingActionBarRightMobile: {
      gap: "8px",
    },
    floatingButton: {
      padding: "14px 24px",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      whiteSpace: "nowrap" as const,
    },
    floatingButtonMobile: {
      padding: "10px 16px",
      fontSize: "13px",
      gap: "6px",
      flexShrink: 0,
      minWidth: "auto",
      height: "42px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    floatingButtonIcon: {
      fontSize: "14px",
    },
    hamburgerButton: {
      padding: "10px 14px",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      backgroundColor: colors.primary,
      color: colors.white,
      fontSize: "18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      flexShrink: 0,
      minWidth: "42px",
      height: "42px",
      marginRight: "8px",
    },
    mobileMenuOverlay: {
      position: "fixed" as const,
      bottom: "70px",
      left: "16px",
      right: "16px",
      backgroundColor: colors.white,
      borderRadius: "16px",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.2)",
      padding: "16px",
      zIndex: 100,
      animation: "slideUp 0.3s ease",
    },
    mobileMenuItem: {
      width: "100%",
      padding: "14px 16px",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "8px",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    ghostCard: {
      backgroundColor: "transparent",
      borderRadius: "16px",
      padding: "40px 20px",
      border: "3px dashed #cbd5e0",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      minHeight: "200px",
      opacity: 0.6,
    },
    ghostCardIcon: {
      fontSize: "48px",
      opacity: 0.5,
    },
    ghostCardText: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#6b7280",
    },
    dragOverlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.65)",
      backdropFilter: "blur(12px)",
      zIndex: 999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none" as const,
      animation: "fadeIn 0.2s ease",
    },
    dragOverlayContent: {
      backgroundColor: "transparent",
      borderRadius: "24px",
      padding: "48px 64px",
      textAlign: "center" as const,
      border: "4px dashed " + colors.primary,
      maxWidth: "500px",
      animation: "pulse 2s ease-in-out infinite",
    },
    dragOverlayIcon: {
      fontSize: "40px",
      marginBottom: "20px",
      display: "block",
      color: colors.primary,
    },
    dragOverlayTitle: {
      fontSize: "28px",
      fontWeight: "bold",
      color: colors.primary,
      marginBottom: "12px",
    },
    dragOverlayText: {
      fontSize: "16px",
      color: colors.gray,
      lineHeight: "1.6",
    },
    contextMenu: {
      position: "absolute" as const,
      backgroundColor: colors.white,
      borderRadius: "12px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      padding: "8px",
      minWidth: "180px",
      zIndex: 1001,
    },
    contextMenuItem: {
      padding: "12px 16px",
      cursor: "pointer",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      transition: "all 0.2s ease",
      border: "none",
      width: "100%",
      backgroundColor: "transparent",
      textAlign: "left" as const,
    },
  };

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FontAwesomeIcon icon={faFile} />;
    if (mimeType.includes("image"))
      return <FontAwesomeIcon icon={faFileImage} />;
    if (mimeType.includes("pdf")) return <FontAwesomeIcon icon={faFilePdf} />;
    if (mimeType.includes("word") || mimeType.includes("doc"))
      return <FontAwesomeIcon icon={faFileWord} />;
    if (mimeType.includes("excel") || mimeType.includes("sheet"))
      return <FontAwesomeIcon icon={faFileExcel} />;
    return <FontAwesomeIcon icon={faFileAlt} />;
  };

  const getSectionInfo = (section: ActiveSection) => {
    switch (section) {
      case "historique":
        return {
          title: "Historique du bâtiment",
          description:
            "L'historique du bâtiment, classé dans l'ordre chronologique, contient les documents liés à celui-ci tels que le titre de propriété, les permis, plans, certificats, preuves d'entretien...",
          icon: <FontAwesomeIcon icon={faClock} />,
        };
      case "travaux":
        return {
          title: "Travaux (DIU)",
          description:
            'Le dossier "Travaux" constitue le DIU. Établir un Dossier d\'Intervention Ultérieure est une obligation légale.',
          icon: <FontAwesomeIcon icon={faHammer} />,
        };
      case "gestion":
        return {
          title: "Gestion courante",
          description:
            "On y classe les contrats d'énergie, d'assurances, de télécom, les baux...",
          icon: <FontAwesomeIcon icon={faFolder} />,
        };
      default:
        return {
          title: "",
          description: "",
          icon: <FontAwesomeIcon icon={faFolder} />,
        };
    }
  };

  const loadAllData = useCallback(
    async (bienId: string, isTabSwitch = false) => {
      if (!bienId) return;

      try {
        if (isTabSwitch) {
          setSectionLoading(true);
        } else {
          setLoading(true);
        }

        const dossiers = await dossiersService.getDossiersByBien(bienId);
        setAllDossiers(dossiers);

        // Charger les notes pour la section et le dossier actuels
        const categorie =
          activeSection === "gestion"
            ? "gestioncourante"
            : activeSection === "travaux"
            ? "travaux"
            : undefined;
        if (categorie) {
          const loadedNotes = await notesService.getNotesByBien(
            bienId,
            currentDossierId || undefined,
            categorie
          );
          setNotes(loadedNotes);
        } else {
          setNotes([]);
        }

        if (activeSection === "historique") {
          const allDocs = await dossiersService.getDocumentsByBien(bienId);
          const historiqueDocs = allDocs.filter(
            (doc) => doc.categorie === "historiquebatiment" || !doc.categorie
          );
          setDocuments(historiqueDocs);
        } else {
          if (currentDossierId) {
            const docs = await dossiersService.getDocumentsByDossier(
              currentDossierId
            );
            setDocuments(docs);
          } else {
            // À la racine : charger les documents sans dossier (null)
            // OU avec dossierId = "root_{bienId}" (format mobile)
            const docs = await dossiersService.getDocumentsByBien(bienId);
            const rootDossierId = `root_${bienId}`;
            const filteredDocs = docs.filter(
              (doc: DocumentItem) =>
                (!doc.dossierId || doc.dossierId === rootDossierId) &&
                doc.categorie ===
                  (activeSection === "gestion" ? "gestioncourante" : "travaux")
            );
            console.log(
              "📁 Documents à la racine filtrés:",
              filteredDocs.length,
              "sur",
              docs.length
            );
            setDocuments(filteredDocs);
          }
        }
      } catch (error) {
        console.error("Erreur chargement:", error);
      } finally {
        setLoading(false);
        setSectionLoading(false);
      }
    },
    [activeSection, currentDossierId]
  );

  useEffect(() => {
    const idFromUrl = params.bienId;
    const fetchBien = async (id: string) => {
      try {
        setLoading(true);
        const fetched = await biensService.getBienById(id);
        if (fetched) {
          setBien(fetched);
          await loadAllData(fetched.id || id);

          // Vérifier si c'est la première visite de ce bien
          const visitedKey = `onboarding_visited_${fetched.id || id}`;
          const hasVisited = localStorage.getItem(visitedKey);
          if (!hasVisited) {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error("Erreur bien:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!propBien && idFromUrl) {
      fetchBien(idFromUrl);
    } else if (propBien) {
      setBien(propBien);
      if (propBien.id) {
        loadAllData(propBien.id);

        // Vérifier si c'est la première visite de ce bien
        const visitedKey = `onboarding_visited_${propBien.id}`;
        const hasVisited = localStorage.getItem(visitedKey);
        if (!hasVisited) {
          setShowOnboarding(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.bienId, propBien]);

  useEffect(() => {
    if (bien?.id) {
      loadAllData(bien.id, true);
    }
  }, [activeSection, currentDossierId, bien?.id, loadAllData]);

  useEffect(() => {
    const items: ListItem[] = [];

    if (activeSection === "historique") {
      documents.forEach((doc) => {
        if (doc.id && doc.createdAt) {
          items.push({
            id: doc.id,
            type: "document",
            nom: doc.nom,
            createdAt: doc.createdAt,
            dateduDocument: doc.dateduDocument,
            mimeType: doc.mimeType,
            url: doc.url,
          });
        }
      });
    } else {
      // Ajouter les dossiers (pas les notes!)
      const dossiersFiltered = allDossiers.filter(
        (d) =>
          d.parentId === currentDossierId &&
          d.categorie ===
            (activeSection === "gestion" ? "gestioncourante" : "travaux")
      );
      dossiersFiltered.forEach((dossier) => {
        if (dossier.id && dossier.createdAt) {
          items.push({
            id: dossier.id,
            type: "dossier",
            nom: dossier.nom,
            description: dossier.description,
            createdAt: dossier.createdAt,
          });
        }
      });

      // Ajouter les notes
      notes.forEach((note) => {
        if (note.id && note.createdAt) {
          items.push({
            id: note.id,
            type: "note",
            nom: note.nom,
            description: note.contenu,
            createdAt: note.createdAt,
          });
        }
      });

      // Ajouter les documents
      documents.forEach((doc) => {
        if (doc.id && doc.createdAt) {
          items.push({
            id: doc.id,
            type: "document",
            nom: doc.nom,
            createdAt: doc.createdAt,
            mimeType: doc.mimeType,
            url: doc.url,
          });
        }
      });
    }

    if (activeSection === "historique") {
      items.sort((a, b) => {
        const dateA = a.dateduDocument || a.createdAt;
        const dateB = b.dateduDocument || b.createdAt;
        return dateB.getTime() - dateA.getTime();
      });
    } else {
      items.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }

    setVisibleItems(items);
  }, [allDossiers, documents, notes, currentDossierId, activeSection]);

  const navigateToFolder = (dossier: Dossier) => {
    if (!dossier.id) return;
    const newPath = [...currentPath, dossier.nom];
    setCurrentPath(newPath);
    setCurrentDossierId(dossier.id);
  };

  const navigateToPath = (index: number) => {
    if (index === -1) {
      setCurrentPath([]);
      setCurrentDossierId(null);
    } else {
      const newPath = currentPath.slice(0, index + 1);
      setCurrentPath(newPath);

      let targetId: string | null = null;
      for (let i = 0; i < newPath.length; i++) {
        const folderName = newPath[i];
        const currentTargetId: string | null = targetId;
        const folder: Dossier | undefined = allDossiers.find(
          (d) =>
            d.nom === folderName &&
            (currentTargetId === null
              ? !d.parentId
              : d.parentId === currentTargetId)
        );
        if (folder?.id) {
          targetId = folder.id;
        }
      }
      setCurrentDossierId(targetId);
    }
  };

  const createDossier = async () => {
    if (!newDossierName.trim() || !bien?.id) return;

    try {
      setIsCreatingDossier(true);
      await dossiersService.creerDossier({
        nom: newDossierName,
        description: newDossierDescription,
        bienId: bien.id,
        parentId: currentDossierId,
        categorie: activeSection === "gestion" ? "gestioncourante" : "travaux",
      });

      setNewDossierName("");
      setNewDossierDescription("");
      setShowCreateDossierModal(false);
      await loadAllData(bien.id);
    } catch (error: any) {
      alert("Erreur: " + error.message);
    } finally {
      setIsCreatingDossier(false);
    }
  };

  const createNote = async () => {
    if (!newNoteName.trim() || !bien?.id) return;

    try {
      setIsCreatingNote(true);
      const categorie =
        activeSection === "gestion" ? "gestioncourante" : "travaux";
      await notesService.creerNote({
        nom: newNoteName,
        contenu: newNoteContent,
        bienId: bien.id,
        dossierId: currentDossierId || `root_${bien.id}`,
        categorie: categorie,
      });

      setNewNoteName("");
      setNewNoteContent("");
      setShowCreateNoteModal(false);
      await loadAllData(bien.id);
    } catch (error: any) {
      alert("Erreur: " + error.message);
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!files.length || !bien?.id) return;

    // Ouvrir le modal de preview au lieu d'uploader directement
    const filesArray = Array.from(files);

    const filteredFiles = filesArray.filter(isFileAllowed);

    if (filteredFiles.length !== filesArray.length) {
      alert(
        "Certains fichiers ont été ignorés car ils ne sont pas autorisés ou dépassent 10 Mo."
      );
    }

    if (!filteredFiles.length) return;

    setUploadingFiles(filteredFiles);

    // setUploadingFiles(filesArray);
    setUploadDestination({
      section: activeSection,
      dossierId: currentDossierId,
    });

    // Initialiser les noms et dates par défaut
    const names: { [key: string]: string } = {};
    const dates: { [key: string]: string } = {};
    filesArray.forEach((file, index) => {
      const fileKey = `${index}`;
      names[fileKey] = file.name.replace(/\.[^/.]+$/, ""); // Nom sans extension
      dates[fileKey] = new Date().toISOString().split("T")[0]; // Date du jour
    });
    setUploadFileNames(names);
    setUploadFileDates(dates);

    setShowUploadPreviewModal(true);
    setShowAddDocumentModal(false);
  };

  // Fonction pour confirmer et uploader les fichiers avec progress
  const confirmAndUploadFiles = async () => {
    if (!uploadingFiles.length || !bien?.id || !uploadDestination) return;

    const progress: { [key: string]: number } = {};
    uploadingFiles.forEach((_, index) => {
      progress[`${index}`] = 0;
    });
    setUploadProgress(progress);

    for (let index = 0; index < uploadingFiles.length; index++) {
      const file = uploadingFiles[index];
      const fileKey = `${index}`;

      try {
        // Upload vers Firebase Storage
        setUploadProgress((prev) => ({ ...prev, [fileKey]: 30 }));

        const categorie =
          uploadDestination.section === "historique"
            ? "historiquebatiment"
            : uploadDestination.section === "gestion"
            ? "gestioncourante"
            : "travaux";

        // Upload le fichier et récupère l'URL Firebase Storage
        const firebaseUrl = await dossiersService.uploadFile(
          file,
          bien.id!,
          categorie
        );

        setUploadProgress((prev) => ({ ...prev, [fileKey]: 70 }));

        const fileExtension = file.name.split(".").pop();
        const customName = uploadFileNames[fileKey] || file.name;
        const finalName = fileExtension
          ? `${customName}.${fileExtension}`
          : customName;

        // Récupérer la date du document si elle a été renseignée
        const fileDateStr = uploadFileDates[fileKey];
        const dateduDocument = fileDateStr ? new Date(fileDateStr) : undefined;

        await dossiersService.ajouterDocument({
          nom: finalName,
          url: firebaseUrl, // URL Firebase Storage au lieu de base64
          dossierId: uploadDestination.dossierId,
          bienId: bien.id!,
          fileType: file.type,
          fileName: finalName,
          fileSize: file.size,
          categorie: categorie,
          dateduDocument: dateduDocument,
        });

        setUploadProgress((prev) => ({ ...prev, [fileKey]: 100 }));
      } catch (error: any) {
        console.error("Erreur upload:", error);
        alert(`Erreur upload ${file.name}: ${error.message}`);
      }
    }

    // Attendre un peu pour voir la progression complète
    await new Promise((resolve) => setTimeout(resolve, 500));
    await loadAllData(bien.id!);

    // Réinitialiser
    setShowUploadPreviewModal(false);
    setUploadingFiles([]);
    setUploadFileNames({});
    setUploadFileDates({});
    setUploadProgress({});
    setUploadDestination(null);
  };

  // Gestion du drag & drop global
  const handleDragEnter = (e: React.DragEvent) => {
    setIsDragging(true);
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setDraggedFiles(e.dataTransfer.files);
      //setShowDropModal(true);

      const filesArray = Array.from(e.dataTransfer.files);
      const filteredFiles = filesArray.filter(isFileAllowed);

      if (filteredFiles.length !== filesArray.length) {
        alert(
          "Certains fichiers ont été ignorés car ils ne sont pas autorisés ou dépassent 10 Mo."
        );
      }

      if (!filteredFiles.length) return;

      setUploadingFiles(filteredFiles);

      try {
        const filesArray = Array.from(e.dataTransfer.files);
        setUploadingFiles(filesArray);
        setUploadDestination({
          section: activeSection,
          dossierId: currentDossierId,
        });

        // Initialiser les noms et dates par défaut
        const names: { [key: string]: string } = {};
        const dates: { [key: string]: string } = {};
        filesArray.forEach((file, index) => {
          const fileKey = `${index}`;
          names[fileKey] = file.name.replace(/\.[^/.]+$/, ""); // Nom sans extension
          dates[fileKey] = new Date().toISOString().split("T")[0]; // Date du jour
        });
        setUploadFileNames(names);
        setUploadFileDates(dates);

        setShowUploadPreviewModal(true);
      } catch (error) {
        console.error("Erreur traitement fichiers droppés:", error);
      }
      e.dataTransfer.clearData();
    }
  };

  // Upload des fichiers droppés
  const uploadDroppedFiles = async () => {
    if (!draggedFiles || !bien?.id || !dropDestination) return;

    for (const file of Array.from(draggedFiles)) {
      try {
        const categorie =
          dropDestination === "historique"
            ? "historiquebatiment"
            : dropDestination === "gestion"
            ? "gestioncourante"
            : "travaux";

        // Upload vers Firebase Storage
        const firebaseUrl = await dossiersService.uploadFile(
          file,
          bien.id!,
          categorie
        );

        await dossiersService.ajouterDocument({
          nom: file.name,
          url: firebaseUrl, // URL Firebase Storage
          dossierId: currentDossierId,
          bienId: bien.id!,
          fileType: file.type,
          fileName: file.name,
          fileSize: file.size,
          categorie: categorie,
        });
      } catch (error: any) {
        console.error("Erreur upload:", error);
      }
    }

    setShowDropModal(false);
    setDraggedFiles(null);
    setDropDestination(null);
    setDropDossierId(null);
    await loadAllData(bien.id);
  };

  // Fonction de renommage
  const handleRename = async () => {
    if (!renamingItem || !newItemName.trim() || !bien?.id) return;

    try {
      if (renamingItem.type === "dossier") {
        await dossiersService.modifierDossier(renamingItem.id, {
          nom: newItemName,
          description: newItemDescription,
        });
      } else {
        await dossiersService.renommerDocument(renamingItem.id, newItemName);
      }

      setShowRenameModal(false);
      setRenamingItem(null);
      setNewItemName("");
      setNewItemDescription("");
      await loadAllData(bien.id);
    } catch (error: any) {
      alert("Erreur renommage: " + error.message);
    }
  };

  // Fonction de suppression
  const handleDelete = async () => {
    if (!itemToDelete || !bien?.id) return;

    try {
      if (itemToDelete.type === "dossier") {
        await dossiersService.supprimerDossier(itemToDelete.id);
      } else {
        await dossiersService.supprimerDocument(itemToDelete.id);
      }

      setShowDeleteModal(false);
      setItemToDelete(null);
      await loadAllData(bien.id);
    } catch (error: any) {
      alert("Erreur suppression: " + error.message);
    }
  };

  // Fonction pour compléter l'onboarding
  const completeOnboarding = () => {
    if (bien?.id) {
      const visitedKey = `onboarding_visited_${bien.id}`;
      localStorage.setItem(visitedKey, "true");
    }
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            color: colors.gray,
            fontSize: "18px",
          }}
        >
          Chargement...
        </div>
      </div>
    );
  }

  if (!bien) {
    return (
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            color: colors.gray,
            fontSize: "18px",
          }}
        >
          Bien introuvable
        </div>
      </div>
    );
  }

  const sectionInfo = getSectionInfo(activeSection);

  return (
    <div
      style={styles.container}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Pattern overlay pour image par défaut */}
      <div style={styles.patternOverlay}></div>

      {/* Overlay de drag & drop */}
      {isDragging && (
        <div style={styles.dragOverlay} className="drag-overlay-animate">
          <div style={styles.dragOverlayContent}>
            <span style={styles.dragOverlayIcon}>
              <FontAwesomeIcon icon={faCloudUploadAlt} size="1x" />
            </span>
            <h3 style={styles.dragOverlayTitle}>Déposez vos fichiers ici</h3>
            <p style={styles.dragOverlayText}>
              Le document sera disponible dans le dossier actuel.
            </p>
          </div>
        </div>
      )}

      <div style={styles.heroHeader}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroActions}>
          <button
            style={styles.backButton}
            onClick={() => (onClose ? onClose() : navigate(-1))}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
              e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)";
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          {activeSection !== "historique" && (
            <button
              style={styles.viewToggle}
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.08)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)";
              }}
            >
              <FontAwesomeIcon icon={viewMode === "grid" ? faList : faTh} />
            </button>
          )}
        </div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>{bien.nom}</h1>
          <p style={styles.heroSubtitle}>Carnet de vie du bâtiment</p>
          <div style={styles.heroBadge}>{bien.type}</div>
        </div>
      </div>

      <div style={styles.navigationHeader}>
        <div style={styles.sectionTabs}>
          {(["travaux", "gestion", "historique"] as ActiveSection[]).map(
            (section) => (
              <button
                key={section}
                style={{
                  ...styles.sectionTab,
                  ...(activeSection === section ? styles.activeTab : {}),
                }}
                onClick={() => {
                  setActiveSection(section);
                  setCurrentPath([]);
                  setCurrentDossierId(null);
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== section) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(139, 69, 19, 0.02)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {getSectionInfo(section).icon} {getSectionInfo(section).title}
              </button>
            )
          )}
        </div>

        {activeSection !== "historique" && currentPath.length > 0 && (
          <div style={styles.breadcrumb}>
            <button
              style={styles.breadcrumbButton}
              onClick={() => navigateToPath(-1)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(139, 69, 19, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <FontAwesomeIcon icon={faHome} style={{ marginRight: "6px" }} />
              Racine
            </button>
            {currentPath.map((folder, index) => (
              <React.Fragment key={index}>
                <span>›</span>
                <button
                  style={{
                    ...styles.breadcrumbButton,
                    ...(index === currentPath.length - 1
                      ? {
                          backgroundColor: colors.light,
                          color: colors.text,
                        }
                      : {}),
                  }}
                  onClick={() => navigateToPath(index)}
                  onMouseEnter={(e) => {
                    if (index !== currentPath.length - 1) {
                      e.currentTarget.style.backgroundColor =
                        "rgba(139, 69, 19, 0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentPath.length - 1) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {folder}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div style={styles.content}>
        <div style={styles.sectionDescription}>
          <h2 style={styles.sectionTitle}>{sectionInfo.title}</h2>
          <p style={styles.sectionSubtitle}>{sectionInfo.description}</p>
        </div>

        {sectionLoading ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "60px 0",
            color: colors.gray,
            fontSize: "16px",
            flexDirection: "column" as const,
            gap: "12px",
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              border: `3px solid ${colors.primary}`,
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : activeSection === "historique" ? (
          <div style={styles.timelineContainer}>
            <div style={styles.timelineActions}>
              <button
                style={{
                  ...styles.actionButton,
                  ...styles.primaryButton,
                  margin: "0 auto",
                  display: "block",
                }}
                onClick={() => setShowAddDocumentModal(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(139, 69, 19, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                <FontAwesomeIcon
                  icon={faUpload}
                  style={{ marginRight: "8px" }}
                />
                Ajouter un document à l'historique
              </button>
            </div>

            {visibleItems.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <FontAwesomeIcon icon={faScroll} size="3x" />
                </div>
                <h3>Aucun document dans l'historique</h3>
                <p>
                  Commencez par ajouter des documents pour créer l'historique de
                  votre bâtiment
                </p>
              </div>
            ) : (
              <div style={styles.timeline}>
                {visibleItems.map((item, index) => (
                  <div key={item.id} style={styles.timelineItem}>
                    <div style={styles.timelineLine}>
                      <div style={styles.timelineDot}></div>
                      {index < visibleItems.length - 1 && (
                        <div style={styles.timelineConnector}></div>
                      )}
                    </div>
                    <div style={styles.timelineContent}>
                      <div
                        style={styles.timelineCard}
                        onClick={() => {
                          const doc = documents.find((d) => d.id === item.id);
                          setSelectedDocument(doc || null);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 24px rgba(0,0,0,0.12)";
                          e.currentTarget.style.cursor = "pointer";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 16px rgba(0,0,0,0.08)";
                        }}
                      >
                        <div style={styles.timelineCardHeader}>
                          <div style={styles.timelineDocIcon}>
                            {getFileIcon(item.mimeType)}
                          </div>
                          <div style={styles.timelineDocInfo}>
                            <h4 style={styles.timelineDocName}>{item.nom}</h4>
                            <p style={styles.timelineDocDate}>
                              {(item.dateduDocument || item.createdAt).toLocaleDateString("fr-FR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </p>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              style={styles.timelineDocAction}
                              onClick={(e) => {
                                e.stopPropagation();
                                const doc = documents.find(
                                  (d) => d.id === item.id
                                );
                                setSelectedDocument(doc || null);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  colors.primary;
                                e.currentTarget.style.color = colors.white;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f3f4f6";
                                e.currentTarget.style.color = "inherit";
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              style={styles.timelineDocAction}
                              onClick={(e) => {
                                e.stopPropagation();
                                const doc = documents.find(
                                  (d) => d.id === item.id
                                );
                                if (doc) {
                                  setEditingDocument(doc);
                                  setEditDocumentName(doc.nom.replace(/\.[^/.]+$/, ""));
                                  setEditDocumentDate(
                                    doc.dateduDocument
                                      ? doc.dateduDocument.toISOString().split("T")[0]
                                      : doc.createdAt
                                      ? doc.createdAt.toISOString().split("T")[0]
                                      : new Date().toISOString().split("T")[0]
                                  );
                                  setShowEditDocumentModal(true);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  colors.primary;
                                e.currentTarget.style.color = colors.white;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f3f4f6";
                                e.currentTarget.style.color = "inherit";
                              }}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              style={styles.timelineDocAction}
                              onClick={(e) => {
                                e.stopPropagation();
                                const doc = documents.find(
                                  (d) => d.id === item.id
                                );
                                if (doc) {
                                  setItemToDelete({
                                    id: doc.id!,
                                    type: "document",
                                    name: doc.nom,
                                  });
                                  setShowDeleteModal(true);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#ef4444";
                                e.currentTarget.style.color = colors.white;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f3f4f6";
                                e.currentTarget.style.color = "inherit";
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {visibleItems.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <FontAwesomeIcon icon={faFolder} size="3x" />
                </div>
                <h3>{currentDossierId ? "Dossier vide" : "Aucun élément"}</h3>
                <p>
                  {currentDossierId
                    ? "Ajoutez des fichiers ou créez des sous-dossiers"
                    : "Créez des dossiers pour organiser vos documents"}
                </p>
              </div>
            ) : (
              <div style={styles.itemsGrid}>
                {visibleItems.map((item) => (
                  <div
                    key={item.id}
                    style={{ ...styles.itemCard, position: "relative" }}
                    onClick={() => {
                      if (item.type === "dossier") {
                        const dossier = allDossiers.find(
                          (d) => d.id === item.id
                        );
                        if (dossier) navigateToFolder(dossier);
                      } else if (item.type === "note") {
                        const note = notes.find((n) => n.id === item.id);
                        if (note) {
                          setSelectedNote(note);
                          setShowNoteModal(true);
                        }
                      } else if (item.type === "document") {
                        const doc = documents.find((d) => d.id === item.id);
                        setSelectedDocument(doc || null);
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 24px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 12px rgba(0,0,0,0.06)";
                    }}
                  >
                    {/* Bouton menu 3 points pour tous les types */}
                    <button
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        padding: "6px",
                        border: "none",
                        borderRadius: "6px",
                        backgroundColor: "#f3f4f6",
                        cursor: "pointer",
                        fontSize: "14px",
                        opacity: 0.7,
                        transition: "all 0.2s ease",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.type === "note") {
                          setShowNoteMenu(
                            showNoteMenu === item.id ? null : item.id
                          );
                        } else {
                          setShowItemMenu(
                            showItemMenu === item.id ? null : item.id
                          );
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary;
                        e.currentTarget.style.color = colors.white;
                        e.currentTarget.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                        e.currentTarget.style.color = "inherit";
                        e.currentTarget.style.opacity = "0.7";
                      }}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>

                    {/* Menu contextuel pour notes */}
                    {item.type === "note" && showNoteMenu === item.id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "44px",
                          right: "8px",
                          backgroundColor: colors.white,
                          borderRadius: "12px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          minWidth: "160px",
                          overflow: "hidden",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: colors.text,
                            transition: "background-color 0.2s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const note = notes.find((n) => n.id === item.id);
                            if (note) {
                              setEditingNote(note);
                              setNewNoteName(note.nom);
                              setNewNoteContent(note.contenu);
                              setShowEditNoteModal(true);
                              setShowNoteMenu(null);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            style={{ marginRight: "10px" }}
                          />
                          Modifier la note
                        </button>
                        <button
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#EF4444",
                            transition: "background-color 0.2s ease",
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                `Supprimer la note "${item.nom}" ?`
                              )
                            ) {
                              try {
                                await notesService.supprimerNote(item.id);
                                setShowNoteMenu(null);
                                if (bien?.id) await loadAllData(bien.id);
                              } catch (error: any) {
                                alert("Erreur: " + error.message);
                              }
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FEE2E2";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ marginRight: "10px" }}
                          />
                          Supprimer la note
                        </button>
                      </div>
                    )}

                    {/* Menu contextuel pour dossiers et documents */}
                    {item.type !== "note" && showItemMenu === item.id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "44px",
                          right: "8px",
                          backgroundColor: colors.white,
                          borderRadius: "12px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          minWidth: "180px",
                          overflow: "hidden",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Option pour voir le contenu si dossier avec sous-éléments */}
                        {item.type === "dossier" &&
                          (() => {
                            const subDossiers = getSubDossiers(item.id);
                            const subDocuments = getDocumentsInDossier(item.id);
                            const totalSubs =
                              subDossiers.length + subDocuments.length;

                            if (totalSubs === 0) return null;

                            const isExpanded = expandedDossiers.has(item.id);

                            return (
                              <button
                                style={{
                                  width: "100%",
                                  padding: "12px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  color: colors.primary,
                                  fontWeight: "600",
                                  transition: "background-color 0.2s ease",
                                  borderBottom: "1px solid #f3f4f6",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDossierExpansion(item.id);
                                  setShowItemMenu(null);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(139, 69, 19, 0.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={isExpanded ? faEye : faFolder}
                                  style={{ marginRight: "10px" }}
                                />
                                {isExpanded ? "Masquer" : "Voir"} le contenu (
                                {totalSubs})
                              </button>
                            );
                          })()}
                        <button
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: colors.text,
                            transition: "background-color 0.2s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const dossier =
                              item.type === "dossier"
                                ? allDossiers.find((d) => d.id === item.id)
                                : null;
                            setRenamingItem({
                              id: item.id,
                              type:
                                item.type === "document"
                                  ? "document"
                                  : "dossier",
                              currentName: item.nom,
                              currentDescription: dossier?.description || "",
                            });
                            setNewItemName(item.nom);
                            setNewItemDescription(dossier?.description || "");
                            setShowRenameModal(true);
                            setShowItemMenu(null);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            style={{ marginRight: "10px" }}
                          />
                          Modifier
                        </button>
                        <button
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#EF4444",
                            transition: "background-color 0.2s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setItemToDelete({
                              id: item.id,
                              type:
                                item.type === "document"
                                  ? "document"
                                  : "dossier",
                              name: item.nom,
                            });
                            setShowDeleteModal(true);
                            setShowItemMenu(null);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FEE2E2";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ marginRight: "10px" }}
                          />
                          Supprimer
                        </button>
                      </div>
                    )}

                    <div style={styles.itemIcon}>
                      {item.type === "note" ? (
                        <FontAwesomeIcon
                          icon={faNoteSticky}
                          color={colors.blue}
                        />
                      ) : item.type === "dossier" ? (
                        <FontAwesomeIcon
                          icon={faFolder}
                          color={colors.primary}
                        />
                      ) : item.type === "document" ? (
                        getFileIcon(item.mimeType)
                      ) : (
                        <FontAwesomeIcon
                          icon={faNoteSticky}
                          color={colors.blue}
                        />
                      )}
                    </div>
                    <div style={styles.itemName}>{item.nom}</div>
                    {item.description && (
                      <div style={styles.itemDescription}>
                        {item.description}
                      </div>
                    )}
                    <div style={styles.itemDescription}>
                      {item.createdAt.toLocaleDateString("fr-FR")}
                    </div>

                    {/* Badge pour les sous-dossiers */}
                    {item.type === "dossier" &&
                      (() => {
                        const subDossiers = getSubDossiers(item.id);
                        const subDocuments = getDocumentsInDossier(item.id);
                        const totalSubs =
                          subDossiers.length + subDocuments.length;
                        const isExpanded = expandedDossiers.has(item.id);

                        return (
                          <div
                            style={{
                              marginTop: "12px",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              textAlign: "center",
                              cursor: totalSubs > 0 ? "pointer" : "default",
                              backgroundColor:
                                totalSubs > 0
                                  ? "rgba(139, 69, 19, 0.08)"
                                  : "rgba(0, 0, 0, 0.03)",
                              color:
                                totalSubs > 0
                                  ? colors.primary
                                  : "rgba(0, 0, 0, 0.3)",
                              transition: "all 0.2s ease",
                            }}
                            onClick={(e) => {
                              if (totalSubs > 0) {
                                e.stopPropagation();
                                toggleDossierExpansion(item.id);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (totalSubs > 0) {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(139, 69, 19, 0.15)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (totalSubs > 0) {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(139, 69, 19, 0.08)";
                              }
                            }}
                          >
                            {totalSubs === 0
                              ? "Pas de sous-dossier"
                              : `${
                                  isExpanded ? "▼" : "▶"
                                } ${totalSubs} sous-dossier${
                                  totalSubs > 1 ? "s" : ""
                                }`}
                          </div>
                        );
                      })()}

                    {/* Affichage des sous-dossiers si déplié */}
                    {item.type === "dossier" &&
                      expandedDossiers.has(item.id) &&
                      (() => {
                        const subDossiers = getSubDossiers(item.id);
                        const subDocuments = getDocumentsInDossier(item.id);
                        const totalSubs =
                          subDossiers.length + subDocuments.length;

                        if (totalSubs === 0) return null;

                        return (
                          <>
                            <div style={styles.subItemsContainer}>
                              {subDossiers.map((subDossier) => (
                                <div
                                  key={subDossier.id}
                                  style={styles.subItem}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToFolder(subDossier);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(139, 69, 19, 0.08)";
                                    e.currentTarget.style.transform =
                                      "translateX(4px)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(139, 69, 19, 0.03)";
                                    e.currentTarget.style.transform =
                                      "translateX(0)";
                                  }}
                                >
                                  <span style={{ marginRight: "8px" }}>
                                    <FontAwesomeIcon icon={faFolder} />
                                  </span>
                                  <span style={{ fontWeight: "600" }}>
                                    {subDossier.nom}
                                  </span>
                                </div>
                              ))}
                              {subDocuments.map((subDoc) => (
                                <div
                                  key={subDoc.id}
                                  style={styles.subItem}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDocument(subDoc);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(139, 69, 19, 0.08)";
                                    e.currentTarget.style.transform =
                                      "translateX(4px)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(139, 69, 19, 0.03)";
                                    e.currentTarget.style.transform =
                                      "translateX(0)";
                                  }}
                                >
                                  <span>{getFileIcon(subDoc.fileType)}</span>
                                  <span style={{ fontWeight: "600" }}>
                                    {subDoc.nom}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                  </div>
                ))}

                {/* Card fantôme pour créer un nouveau dossier - à la fin */}
                <div
                  style={styles.ghostCard}
                  onClick={() => setShowCreateDossierModal(true)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.backgroundColor =
                      "rgba(139, 69, 19, 0.02)";
                    e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e0";
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.opacity = "0.6";
                  }}
                >
                  <div style={styles.ghostCardIcon}>➕</div>
                  <div style={styles.ghostCardText}>Nouveau dossier</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barre d'actions flottante */}
      {activeSection !== "historique" && (
        <>
          {/* Menu mobile overlay */}
          {isMobile && showMobileMenu && (
            <>
              <div
                style={{
                  position: "fixed" as const,
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.4)",
                  zIndex: 99,
                  backdropFilter: "blur(2px)",
                }}
                onClick={() => setShowMobileMenu(false)}
              />
              <div style={styles.mobileMenuOverlay}>
                <button
                  style={{ ...styles.mobileMenuItem, ...styles.primaryButton }}
                  onClick={() => {
                    setShowCreateDossierModal(true);
                    setShowMobileMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon={faFolderPlus} />
                  Nouveau dossier
                </button>

                <button
                  style={{
                    ...styles.mobileMenuItem,
                    ...styles.secondaryButton,
                  }}
                  onClick={() => {
                    setShowAddDocumentModal(true);
                    setShowMobileMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  Ajouter un document
                </button>

                {(activeSection === "gestion" ||
                  activeSection === "travaux") && (
                  <button
                    style={{ ...styles.mobileMenuItem, ...styles.noteButton }}
                    onClick={() => {
                      setShowCreateNoteModal(true);
                      setShowMobileMenu(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faNoteSticky} />
                    Créer une note
                  </button>
                )}

                <button
                  style={{
                    ...styles.mobileMenuItem,
                    backgroundColor:
                      viewMode === "grid" ? colors.primary : "#f3f4f6",
                    color: viewMode === "grid" ? colors.white : colors.text,
                  }}
                  onClick={() => {
                    setViewMode(viewMode === "grid" ? "list" : "grid");
                    setShowMobileMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon={viewMode === "grid" ? faList : faTh} />
                  {viewMode === "grid" ? "Mode Liste" : "Mode Grille"}
                </button>
              </div>
            </>
          )}

          {/* Barre d'actions */}
          <div
            style={{
              ...styles.floatingActionBar,
              ...(isMobile ? styles.floatingActionBarMobile : {}),
            }}
          >
            {isMobile ? (
              // Version mobile avec hamburger
              <>
                <button
                  style={styles.hamburgerButton}
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  <FontAwesomeIcon
                    icon={showMobileMenu ? faTimes : faEllipsisV}
                  />
                </button>

                <div
                  style={{
                    ...styles.floatingActionBarLeft,
                    ...styles.floatingActionBarLeftMobile,
                  }}
                >
                  <button
                    style={{
                      ...styles.floatingButton,
                      ...styles.floatingButtonMobile,
                      ...styles.primaryButton,
                    }}
                    onClick={() => setShowCreateDossierModal(true)}
                  >
                    <FontAwesomeIcon
                      icon={faFolderPlus}
                      style={styles.floatingButtonIcon}
                    />
                    <span>Dossier</span>
                  </button>

                  <button
                    style={{
                      ...styles.floatingButton,
                      ...styles.floatingButtonMobile,
                      ...styles.secondaryButton,
                    }}
                    onClick={() => setShowAddDocumentModal(true)}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      style={styles.floatingButtonIcon}
                    />
                    <span>Document</span>
                  </button>

                  {(activeSection === "gestion" ||
                    activeSection === "travaux") && (
                    <button
                      style={{
                        ...styles.floatingButton,
                        ...styles.floatingButtonMobile,
                        ...styles.noteButton,
                      }}
                      onClick={() => setShowCreateNoteModal(true)}
                    >
                      <FontAwesomeIcon
                        icon={faNoteSticky}
                        style={styles.floatingButtonIcon}
                      />
                      <span>Note</span>
                    </button>
                  )}
                </div>

                <button
                  style={{
                    ...styles.floatingButton,
                    ...styles.floatingButtonMobile,
                    backgroundColor:
                      viewMode === "grid" ? colors.primary : "#f3f4f6",
                    color: viewMode === "grid" ? colors.white : colors.text,
                    marginLeft: "8px",
                    minWidth: "42px",
                    padding: "10px",
                  }}
                  onClick={() =>
                    setViewMode(viewMode === "grid" ? "list" : "grid")
                  }
                >
                  <FontAwesomeIcon
                    icon={viewMode === "grid" ? faList : faTh}
                    style={styles.floatingButtonIcon}
                  />
                </button>
              </>
            ) : (
              // Version desktop (originale)
              <>
                <div style={styles.floatingActionBarLeft}>
                  <button
                    style={{
                      ...styles.floatingButton,
                      ...styles.primaryButton,
                    }}
                    onClick={() => setShowCreateDossierModal(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 24px rgba(139, 69, 19, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 12px rgba(0,0,0,0.1)";
                    }}
                  >
                    <FontAwesomeIcon icon={faFolderPlus} />
                    Nouveau dossier
                  </button>

                  <button
                    style={{
                      ...styles.floatingButton,
                      ...styles.secondaryButton,
                    }}
                    onClick={() => setShowAddDocumentModal(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 24px rgba(34, 197, 94, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 12px rgba(0,0,0,0.1)";
                    }}
                  >
                    <FontAwesomeIcon icon={faUpload} />
                    Ajouter un document
                  </button>

                  {(activeSection === "gestion" ||
                    activeSection === "travaux") && (
                    <button
                      style={{ ...styles.floatingButton, ...styles.noteButton }}
                      onClick={() => setShowCreateNoteModal(true)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 24px rgba(34, 197, 94, 0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 12px rgba(0,0,0,0.1)";
                      }}
                    >
                      <FontAwesomeIcon icon={faNoteSticky} />
                      Créer note
                    </button>
                  )}
                </div>

                <div style={styles.floatingActionBarRight}>
                  <button
                    style={{
                      ...styles.floatingButton,
                      backgroundColor:
                        viewMode === "grid" ? colors.primary : "#f3f4f6",
                      color: viewMode === "grid" ? colors.white : colors.text,
                    }}
                    onClick={() =>
                      setViewMode(viewMode === "grid" ? "list" : "grid")
                    }
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <FontAwesomeIcon
                      icon={viewMode === "grid" ? faList : faTh}
                    />
                    {viewMode === "grid" ? "Liste" : "Grille"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Modal drag & drop */}
      {showDropModal && draggedFiles && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDropModal(false);
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                Où déposer {draggedFiles.length} fichier
                {draggedFiles.length > 1 ? "s" : ""} ?
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              {(["historique", "travaux", "gestion"] as ActiveSection[]).map(
                (section) => (
                  <button
                    key={section}
                    style={{
                      ...styles.button,
                      ...(dropDestination === section
                        ? styles.saveButton
                        : { backgroundColor: "#f3f4f6", color: colors.text }),
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                    onClick={() => setDropDestination(section)}
                  >
                    <span style={{ fontSize: "24px" }}>
                      {getSectionInfo(section).icon}
                    </span>
                    <div>
                      <div style={{ fontWeight: "700" }}>
                        {getSectionInfo(section).title}
                      </div>
                    </div>
                  </button>
                )
              )}
            </div>

            {dropDestination && dropDestination !== "historique" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Dossier (optionnel)
                </label>
                <select
                  style={{
                    ...styles.input,
                    cursor: "pointer",
                  }}
                  value={dropDossierId || ""}
                  onChange={(e) => setDropDossierId(e.target.value || null)}
                >
                  <option value="">Racine</option>
                  {allDossiers
                    .filter(
                      (d) =>
                        d.categorie ===
                        (dropDestination === "gestion"
                          ? "gestioncourante"
                          : "travaux")
                    )
                    .map((dossier) => (
                      <option key={dossier.id} value={dossier.id}>
                        {dossier.nom}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowDropModal(false);
                  setDraggedFiles(null);
                  setDropDestination(null);
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={uploadDroppedFiles}
                disabled={!dropDestination}
              >
                Uploader
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal renommage */}
      {showRenameModal && renamingItem && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRenameModal(false);
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                Modifier{" "}
                {renamingItem.type === "dossier" ? "le dossier" : "le document"}
              </div>
              <p style={{ color: colors.gray, margin: 0 }}>
                {renamingItem.currentName}
              </p>
            </div>

            <input
              style={styles.input}
              type="text"
              placeholder="Nouveau nom"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              autoFocus
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />

            {renamingItem.type === "dossier" && (
              <textarea
                style={styles.textarea}
                placeholder="Description (optionnel)"
                value={newItemDescription}
                onChange={(e) => setNewItemDescription(e.target.value)}
                rows={3}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            )}

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowRenameModal(false);
                  setRenamingItem(null);
                  setNewItemName("");
                  setNewItemDescription("");
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={handleRename}
                disabled={
                  !newItemName.trim() ||
                  (newItemName === renamingItem.currentName &&
                    newItemDescription ===
                      (renamingItem.currentDescription || ""))
                }
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateDossierModal && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateDossierModal(false);
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Nouveau dossier</div>
              <p style={{ color: colors.gray, margin: 0 }}>
                {currentPath.length > 0
                  ? `Dans ${currentPath[currentPath.length - 1]}`
                  : "À la racine"}
              </p>
            </div>
            <input
              style={styles.input}
              type="text"
              placeholder="Nom du dossier"
              value={newDossierName}
              onChange={(e) => setNewDossierName(e.target.value)}
              autoFocus
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <textarea
              style={styles.textarea}
              placeholder="Description (optionnel)"
              value={newDossierDescription}
              onChange={(e) => setNewDossierDescription(e.target.value)}
              rows={3}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowCreateDossierModal(false);
                  setNewDossierName("");
                  setNewDossierDescription("");
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={createDossier}
                disabled={!newDossierName.trim() || isCreatingDossier}
                onMouseEnter={(e) => {
                  if (!(!newDossierName.trim() || isCreatingDossier)) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(139, 69, 19, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                {isCreatingDossier ? "Création..." : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateNoteModal && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateNoteModal(false);
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Nouvelle note</div>
              <p style={{ color: colors.gray, margin: 0 }}>
                {currentPath.length > 0
                  ? `Dans ${currentPath[currentPath.length - 1]}`
                  : "À la racine"}
              </p>
            </div>
            <input
              style={styles.input}
              type="text"
              placeholder="Titre de la note"
              value={newNoteName}
              onChange={(e) => setNewNoteName(e.target.value)}
              autoFocus
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <textarea
              style={styles.textarea}
              placeholder="Contenu de la note"
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={8}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowCreateNoteModal(false);
                  setNewNoteName("");
                  setNewNoteContent("");
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={createNote}
                disabled={!newNoteName.trim() || isCreatingNote}
                onMouseEnter={(e) => {
                  if (!(!newNoteName.trim() || isCreatingNote)) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(139, 69, 19, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                {isCreatingNote ? "Création..." : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddDocumentModal && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddDocumentModal(false);
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Ajouter des fichiers</div>
              <p style={{ color: colors.gray, margin: 0 }}>
                {activeSection === "historique"
                  ? "Dans l'historique du bâtiment"
                  : currentPath.length > 0
                  ? `Dans ${currentPath[currentPath.length - 1]}`
                  : "À la racine"}
              </p>
            </div>
            <input
              style={styles.hiddenInput}
              type="file"
              multiple
              id="file-input"
              accept=".jpg,.jpeg,.png,.svg,.pdf,.txt,.xml,.csv,.json,text/plain,text/xml,application/xml,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.ai"
              onChange={(e) => {
                if (e.target.files) {
                  handleFileUpload(e.target.files);
                }
              }}
            />
            <button
              style={{
                ...styles.actionButton,
                ...styles.primaryButton,
                width: "100%",
                justifyContent: "center",
                padding: "16px",
                fontSize: "16px",
              }}
              onClick={() => document.getElementById("file-input")?.click()}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(139, 69, 19, 0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
              }}
            >
              <FontAwesomeIcon icon={faUpload} style={{ marginRight: "8px" }} />
              Choisir des fichiers
            </button>
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => setShowAddDocumentModal(false)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de preview et upload avec progression */}
      {showUploadPreviewModal && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadPreviewModal(false);
              setUploadingFiles([]);
            }
          }}
        >
          <div
            style={{
              ...styles.modalContent,
              maxWidth: "700px",
              maxHeight: "85vh",
              overflow: "auto",
            }}
          >
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                <FontAwesomeIcon
                  icon={faUpload}
                  style={{ marginRight: "12px" }}
                />
                Préparer l'upload ({uploadingFiles.length} fichier
                {uploadingFiles.length > 1 ? "s" : ""})
              </div>
              <p style={{ color: colors.gray, margin: 0, fontSize: "14px" }}>
                {uploadDestination?.section === "historique"
                  ? "Personnalisez le nom et la date pour chaque document"
                  : "Personnalisez le nom de chaque fichier"}
              </p>
            </div>

            <div style={{ marginBottom: "20px" }}>
              {uploadingFiles.map((file, index) => {
                const fileKey = `${index}`;
                const progress = uploadProgress[fileKey] || 0;
                const isImage = file.type.startsWith("image/");
                const previewUrl = isImage ? URL.createObjectURL(file) : null;

                return (
                  <div key={index} style={styles.uploadPreviewCard}>
                    {isImage && previewUrl && (
                      <img
                        src={previewUrl}
                        alt={file.name}
                        style={styles.uploadPreviewImage}
                      />
                    )}
                    {!isImage && (
                      <div
                        style={{
                          ...styles.uploadPreviewImage,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "48px",
                        }}
                      >
                        {getFileIcon(file.type)}
                      </div>
                    )}

                    <div style={{ marginBottom: "12px" }}>
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: colors.gray,
                          display: "block",
                          marginBottom: "6px",
                        }}
                      >
                        Nom du fichier
                      </label>
                      <input
                        type="text"
                        value={uploadFileNames[fileKey] || ""}
                        onChange={(e) =>
                          setUploadFileNames((prev) => ({
                            ...prev,
                            [fileKey]: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = colors.primary)
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor = "#e5e7eb")
                        }
                        placeholder="Nom du fichier"
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: colors.gray,
                          marginTop: "4px",
                        }}
                      >
                        Extension: .{file.name.split(".").pop()} • Taille:{" "}
                        {(file.size / 1024).toFixed(1)} Ko
                      </div>
                    </div>

                    {uploadDestination?.section === "historique" && (
                      <div style={{ marginBottom: "12px" }}>
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: colors.gray,
                            display: "block",
                            marginBottom: "6px",
                          }}
                        >
                          Date du document
                        </label>
                        <input
                          type="date"
                          value={uploadFileDates[fileKey] || ""}
                          onChange={(e) =>
                            setUploadFileDates((prev) => ({
                              ...prev,
                              [fileKey]: e.target.value,
                            }))
                          }
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "all 0.3s ease",
                          }}
                          onFocus={(e) =>
                            (e.currentTarget.style.borderColor = colors.primary)
                          }
                          onBlur={(e) =>
                            (e.currentTarget.style.borderColor = "#e5e7eb")
                          }
                        />
                      </div>
                    )}

                    {progress > 0 && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: colors.gray,
                            }}
                          >
                            Upload en cours...
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: colors.primary,
                            }}
                          >
                            {progress}%
                          </span>
                        </div>
                        <div style={styles.progressBar}>
                          <div
                            style={{
                              ...styles.progressBarFill,
                              width: `${progress}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowUploadPreviewModal(false);
                  setUploadingFiles([]);
                  setUploadFileNames({});
                  setUploadFileDates({});
                  setUploadProgress({});
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={confirmAndUploadFiles}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(139, 69, 19, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                <FontAwesomeIcon
                  icon={faRocket}
                  style={{ marginRight: "8px" }}
                />
                Uploader ({uploadingFiles.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedDocument && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedDocument(null);
          }}
        >
          <div style={{ ...styles.modalContent, maxWidth: "800px" }}>
            <div style={styles.modalHeader}>
              <div
                style={{
                  ...styles.modalTitle,
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: "1.4",
                }}
              >
                {selectedDocument.nom}
              </div>
              <p
                style={{
                  color: colors.gray,
                  margin: 0,
                  wordWrap: "break-word",
                }}
              >
                {selectedDocument.createdAt?.toLocaleDateString("fr-FR")}
              </p>
            </div>

            {/* Affichage du document */}
            <div
              style={{
                marginBottom: "20px",
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#f8f9fa",
              }}
            >
              {selectedDocument.mimeType?.includes("image") ? (
                // Images
                <img
                  src={selectedDocument.url}
                  alt={selectedDocument.nom}
                  style={{
                    width: "100%",
                    maxHeight: "600px",
                    objectFit: "contain",
                    backgroundColor: "#f8f9fa",
                  }}
                />
              ) : selectedDocument.mimeType?.includes("pdf") ? (
                // PDF avec iframe natif
                <div style={{ position: "relative", height: "600px" }}>
                  <iframe
                    src={`${selectedDocument.url}#toolbar=1&navpanes=1&scrollbar=1`}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title={selectedDocument.nom}
                  />
                </div>
              ) : selectedDocument.mimeType?.includes("word") ||
                selectedDocument.mimeType?.includes("document") ||
                selectedDocument.mimeType?.includes("msword") ||
                selectedDocument.mimeType?.includes(
                  "officedocument.wordprocessingml"
                ) ? (
                // DOCX avec Google Docs Viewer
                <div style={{ position: "relative", height: "600px" }}>
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                      selectedDocument.url
                    )}&embedded=true`}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title={selectedDocument.nom}
                    onError={(e) => {
                      console.error("Erreur chargement document:", e);
                      (e.target as HTMLIFrameElement).style.display = "none";
                      const errorDiv = document.createElement("div");
                      errorDiv.style.cssText =
                        "padding: 60px 40px; text-align: center; color: #ef4444; background: #fef2f2;";
                      errorDiv.innerHTML = `
                        <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
                        <p style="font-size: 16px; font-weight: 600; margin-bottom: 12px;">Impossible de charger la prévisualisation</p>
                        <p style="font-size: 14px; color: #991b1b;">Téléchargez le fichier pour l'ouvrir</p>
                      `;
                      (
                        e.target as HTMLIFrameElement
                      ).parentElement?.appendChild(errorDiv);
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      padding: "8px 12px",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    📄 Document Word
                  </div>
                </div>
              ) : selectedDocument.mimeType?.includes("sheet") ||
                selectedDocument.mimeType?.includes("excel") ||
                selectedDocument.mimeType?.includes("ms-excel") ||
                selectedDocument.mimeType?.includes(
                  "officedocument.spreadsheetml"
                ) ? (
                // Excel avec Google Docs Viewer
                <div style={{ position: "relative", height: "600px" }}>
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                      selectedDocument.url
                    )}&embedded=true`}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title={selectedDocument.nom}
                    onError={(e) => {
                      console.error("Erreur chargement document:", e);
                      (e.target as HTMLIFrameElement).style.display = "none";
                      const errorDiv = document.createElement("div");
                      errorDiv.style.cssText =
                        "padding: 60px 40px; text-align: center; color: #ef4444; background: #fef2f2;";
                      errorDiv.innerHTML = `
                        <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
                        <p style="font-size: 16px; font-weight: 600; margin-bottom: 12px;">Impossible de charger la prévisualisation</p>
                        <p style="font-size: 14px; color: #991b1b;">Téléchargez le fichier pour l'ouvrir</p>
                      `;
                      (
                        e.target as HTMLIFrameElement
                      ).parentElement?.appendChild(errorDiv);
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      padding: "8px 12px",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    📊 Feuille de calcul
                  </div>
                </div>
              ) : selectedDocument.mimeType?.includes("presentation") ||
                selectedDocument.mimeType?.includes("powerpoint") ||
                selectedDocument.mimeType?.includes("ms-powerpoint") ||
                selectedDocument.mimeType?.includes(
                  "officedocument.presentationml"
                ) ? (
                // PowerPoint avec Google Docs Viewer
                <div style={{ position: "relative", height: "600px" }}>
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                      selectedDocument.url
                    )}&embedded=true`}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title={selectedDocument.nom}
                    onError={(e) => {
                      console.error("Erreur chargement document:", e);
                      (e.target as HTMLIFrameElement).style.display = "none";
                      const errorDiv = document.createElement("div");
                      errorDiv.style.cssText =
                        "padding: 60px 40px; text-align: center; color: #ef4444; background: #fef2f2;";
                      errorDiv.innerHTML = `
                        <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
                        <p style="font-size: 16px; font-weight: 600; margin-bottom: 12px;">Impossible de charger la prévisualisation</p>
                        <p style="font-size: 14px; color: #991b1b;">Téléchargez le fichier pour l'ouvrir</p>
                      `;
                      (
                        e.target as HTMLIFrameElement
                      ).parentElement?.appendChild(errorDiv);
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      padding: "8px 12px",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    📽️ Présentation
                  </div>
                </div>
              ) : selectedDocument.mimeType?.includes("video") ? (
                // Vidéos
                <video
                  controls
                  style={{
                    width: "100%",
                    maxHeight: "600px",
                    backgroundColor: "#000",
                  }}
                >
                  <source
                    src={selectedDocument.url}
                    type={selectedDocument.mimeType}
                  />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              ) : selectedDocument.mimeType?.includes("audio") ? (
                // Audio
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <div style={{ fontSize: "64px", marginBottom: "20px" }}>
                    🎵
                  </div>
                  <audio
                    controls
                    style={{
                      width: "100%",
                      maxWidth: "500px",
                    }}
                  >
                    <source
                      src={selectedDocument.url}
                      type={selectedDocument.mimeType}
                    />
                    Votre navigateur ne supporte pas la lecture audio.
                  </audio>
                </div>
              ) : selectedDocument.mimeType?.includes("text") ? (
                // Fichiers texte
                <iframe
                  src={selectedDocument.url}
                  style={{
                    width: "100%",
                    height: "600px",
                    border: "none",
                    backgroundColor: "white",
                    padding: "20px",
                  }}
                  title={selectedDocument.nom}
                />
              ) : (
                // Autres fichiers - Aperçu non disponible
                <div
                  style={{
                    padding: "80px 40px",
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "72px",
                      marginBottom: "24px",
                      opacity: 0.6,
                    }}
                  >
                    {getFileIcon(selectedDocument.mimeType)}
                  </div>
                  <p
                    style={{
                      color: colors.text,
                      fontSize: "20px",
                      fontWeight: "700",
                      marginBottom: "12px",
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      padding: "0 20px",
                    }}
                  >
                    {selectedDocument.nom}
                  </p>
                  <p
                    style={{
                      color: colors.gray,
                      fontSize: "14px",
                      marginBottom: "8px",
                    }}
                  >
                    Type:{" "}
                    {selectedDocument.mimeType ||
                      selectedDocument.fileType ||
                      "document"}
                  </p>
                  <p
                    style={{
                      color: colors.gray,
                      fontSize: "14px",
                      marginBottom: "24px",
                    }}
                  >
                    Taille:{" "}
                    {(
                      (selectedDocument.fileSize ||
                        selectedDocument.taille ||
                        0) / 1024
                    ).toFixed(2)}{" "}
                    Ko
                  </p>
                  <p
                    style={{
                      color: "#f59e0b",
                      fontSize: "13px",
                      padding: "12px 20px",
                      backgroundColor: "rgba(245, 158, 11, 0.1)",
                      borderRadius: "8px",
                      display: "inline-block",
                    }}
                  >
                    ℹ️ Aperçu non disponible pour ce type de fichier.
                    Téléchargez pour l'ouvrir.
                  </p>
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => setSelectedDocument(null)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Fermer
              </button>

              {selectedDocument?.url && (
                <>
                  <button
                    style={{
                      ...styles.button,
                      backgroundColor: "#3b82f6",
                      color: colors.white,
                      fontWeight: "600",
                    }}
                    onClick={() => {
                      if (selectedDocument.url) {
                        navigator.clipboard.writeText(selectedDocument.url);
                        alert("Lien copié dans le presse-papiers !");
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                      e.currentTarget.style.backgroundColor = "#2563eb";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(59, 130, 246, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.backgroundColor = "#3b82f6";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faShare}
                      style={{ marginRight: "8px" }}
                    />
                    Partager
                  </button>

                  <a
                    href={selectedDocument.url}
                    download={selectedDocument.fileName || selectedDocument.nom}
                    style={{
                      ...styles.button,
                      ...styles.saveButton,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(139, 69, 19, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.1)";
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      style={{ marginRight: "8px" }}
                    />
                    Télécharger
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualisation de note */}
      {showNoteModal && selectedNote && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowNoteModal(false);
          }}
        >
          <div style={{ ...styles.modalContent, maxWidth: "700px" }}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                <FontAwesomeIcon
                  icon={faNoteSticky}
                  style={{ marginRight: "12px", color: colors.primary }}
                />
                {selectedNote.nom}
              </div>
              <p style={{ color: colors.gray, margin: 0 }}>
                {selectedNote.createdAt?.toLocaleDateString("fr-FR")}
              </p>
            </div>

            {/* Contenu de la note */}
            <div
              style={{
                marginBottom: "20px",
                padding: "24px",
                backgroundColor: "#fffbf5",
                borderRadius: "12px",
                border: "2px solid rgba(139, 69, 19, 0.1)",
                minHeight: "200px",
                whiteSpace: "pre-wrap",
                fontSize: "15px",
                lineHeight: "1.7",
                color: colors.text,
              }}
            >
              {selectedNote.contenu || "Aucun contenu"}
            </div>

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => setShowNoteModal(false)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Fermer
              </button>

              <button
                style={{
                  ...styles.button,
                  backgroundColor: "#3b82f6",
                  color: colors.white,
                  fontWeight: "600",
                }}
                onClick={() => {
                  setEditingNote(selectedNote);
                  setNewNoteName(selectedNote.nom);
                  setNewNoteContent(selectedNote.contenu);
                  setShowNoteModal(false);
                  setShowEditNoteModal(true);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.backgroundColor = "#2563eb";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(59, 130, 246, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.backgroundColor = "#3b82f6";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <FontAwesomeIcon icon={faEdit} style={{ marginRight: "8px" }} />
                Modifier
              </button>

              <button
                style={{
                  ...styles.button,
                  backgroundColor: "#EF4444",
                  color: colors.white,
                  fontWeight: "600",
                }}
                onClick={async () => {
                  if (
                    window.confirm(`Supprimer la note "${selectedNote.nom}" ?`)
                  ) {
                    try {
                      if (selectedNote.id) {
                        await notesService.supprimerNote(selectedNote.id);
                        setShowNoteModal(false);
                        if (bien?.id) await loadAllData(bien.id);
                      }
                    } catch (error: any) {
                      alert("Erreur: " + error.message);
                    }
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.backgroundColor = "#DC2626";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(239, 68, 68, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.backgroundColor = "#EF4444";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ marginRight: "8px" }}
                />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition de note */}
      {showEditNoteModal && editingNote && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditNoteModal(false);
              setEditingNote(null);
            }
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ marginRight: "12px" }}
                />
                Modifier la note
              </div>
            </div>
            <input
              style={styles.input}
              type="text"
              placeholder="Titre de la note"
              value={newNoteName}
              onChange={(e) => setNewNoteName(e.target.value)}
              autoFocus
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <textarea
              style={styles.textarea}
              placeholder="Contenu de la note"
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={8}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px rgba(139, 69, 19, 0.1)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowEditNoteModal(false);
                  setEditingNote(null);
                  setNewNoteName("");
                  setNewNoteContent("");
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{ ...styles.button, ...styles.saveButton }}
                onClick={async () => {
                  if (!newNoteName.trim() || !editingNote.id) return;
                  try {
                    await notesService.modifierNote(editingNote.id, {
                      nom: newNoteName,
                      contenu: newNoteContent,
                    });
                    setShowEditNoteModal(false);
                    setEditingNote(null);
                    setNewNoteName("");
                    setNewNoteContent("");
                    if (bien?.id) await loadAllData(bien.id);
                  } catch (error: any) {
                    alert("Erreur: " + error.message);
                  }
                }}
                disabled={!newNoteName.trim()}
                onMouseEnter={(e) => {
                  if (newNoteName.trim()) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(139, 69, 19, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && itemToDelete && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDeleteModal(false);
          }}
        >
          <div style={{ ...styles.modalContent, maxWidth: "450px" }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "64px", marginBottom: "16px" }}>⚠️</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: colors.text,
                  marginBottom: "12px",
                }}
              >
                Confirmer la suppression
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  color: colors.gray,
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                Voulez-vous vraiment supprimer{" "}
                {itemToDelete.type === "dossier" ? "le dossier" : "le document"}{" "}
                <strong>"{itemToDelete.name}"</strong> ?
                <br />
                <br />
                {itemToDelete.type === "dossier" && (
                  <span style={{ color: "#dc2626", fontWeight: "600" }}>
                    ⚠️ Attention : Tous les sous-dossiers et documents contenus
                    seront également supprimés.
                  </span>
                )}
                {itemToDelete.type === "document" && (
                  <span>Cette action est irréversible.</span>
                )}
              </p>
            </div>
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{
                  ...styles.button,
                  backgroundColor: "#dc2626",
                  color: colors.white,
                  fontWeight: "700",
                }}
                onClick={handleDelete}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(220, 38, 38, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ marginRight: "8px" }}
                />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition d'un document historique */}
      {showEditDocumentModal && editingDocument && (
        <div
          style={styles.modal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEditDocumentModal(false);
          }}
        >
          <div style={{ ...styles.modalContent, maxWidth: "500px" }}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ marginRight: "12px" }}
                />
                Modifier le document
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: colors.gray,
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Nom du fichier
              </label>
              <input
                type="text"
                value={editDocumentName}
                onChange={(e) => setEditDocumentName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) =>
                  (e.currentTarget.style.borderColor = colors.primary)
                }
                onBlur={(e) =>
                  (e.currentTarget.style.borderColor = "#e5e7eb")
                }
                placeholder="Nom du fichier"
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: colors.gray,
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Date du document
              </label>
              <input
                type="date"
                value={editDocumentDate}
                onChange={(e) => setEditDocumentDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) =>
                  (e.currentTarget.style.borderColor = colors.primary)
                }
                onBlur={(e) =>
                  (e.currentTarget.style.borderColor = "#e5e7eb")
                }
              />
            </div>

            <div style={styles.modalActions}>
              <button
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  setShowEditDocumentModal(false);
                  setEditingDocument(null);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                Annuler
              </button>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                }}
                onClick={async () => {
                  if (!editingDocument?.id || !bien?.id) return;
                  try {
                    const extension = editingDocument.nom.match(/\.[^/.]+$/)?.[0] || "";
                    const finalName = editDocumentName
                      ? `${editDocumentName}${extension}`
                      : editingDocument.nom;
                    const dateduDocument = editDocumentDate
                      ? new Date(editDocumentDate)
                      : undefined;

                    await dossiersService.modifierDocument(editingDocument.id, {
                      nom: finalName,
                      dateduDocument: dateduDocument,
                    });

                    setShowEditDocumentModal(false);
                    setEditingDocument(null);
                    await loadAllData(bien.id);
                  } catch (error: any) {
                    console.error("Erreur modification:", error);
                    alert(`Erreur: ${error.message}`);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(139, 69, 19, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ marginRight: "8px" }}
                />
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'onboarding */}
      {showOnboarding && (
        <div style={styles.modal}>
          <div
            style={{
              ...styles.modalContent,
              maxWidth: "650px",
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Bouton de fermeture */}
            <button
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "none",
                border: "none",
                fontSize: "28px",
                cursor: "pointer",
                color: colors.gray,
                padding: "4px",
                lineHeight: "1",
                transition: "all 0.2s ease",
              }}
              onClick={completeOnboarding}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.text;
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colors.gray;
                e.currentTarget.style.transform = "scale(1)";
              }}
              title="Fermer"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {onboardingStep === 0 && (
              <>
                <div
                  style={{
                    fontSize: "72px",
                    marginBottom: "24px",
                    color: colors.primary,
                  }}
                >
                  <FontAwesomeIcon icon={faHome} />
                </div>
                <h2
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: colors.primary,
                    marginBottom: "16px",
                  }}
                >
                  Bienvenue dans le carnet de vie de votre bâtiment
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: colors.text,
                    lineHeight: "1.8",
                    marginBottom: "24px",
                    textAlign: "left",
                  }}
                >
                  Il est important de pouvoir suivre l'évolution de votre (vos)
                  bien(s) immobilier(s) et de pouvoir retrouver à tous moments
                  les documents inhérents.
                  <br />
                  <br />
                  Grâce au <strong>Carnet de Vie du Bâtiment</strong> de
                  Property Assist, il est désormais possible de classer et
                  stocker tous les documents qui concernent le bâtiment en
                  question, et ce, en{" "}
                  <strong>3 volets distincts et spécifiques</strong>.
                </p>
              </>
            )}

            {onboardingStep === 1 && (
              <>
                <div
                  style={{
                    fontSize: "72px",
                    marginBottom: "24px",
                    color: colors.primary,
                  }}
                >
                  <FontAwesomeIcon icon={faBook} />
                </div>
                <h2
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: colors.primary,
                    marginBottom: "16px",
                  }}
                >
                  Historique du bâtiment
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: colors.text,
                    lineHeight: "1.8",
                    marginBottom: "24px",
                    textAlign: "left",
                  }}
                >
                  L'<strong>historique du bâtiment</strong>, classé dans l'ordre
                  chronologique, contient les documents liés à celui-ci tels que
                  :
                  <br />
                  <br />
                  • Le titre de propriété
                  <br />
                  • Les permis et autorisations
                  <br />
                  • Les plans et certificats
                  <br />
                  • Les preuves d'entretien
                  <br />
                  • Tout document historique important
                  <br />
                  <br />
                  <span style={{ fontStyle: "italic", color: colors.gray }}>
                    Les documents sont automatiquement triés par date pour une
                    meilleure traçabilité.
                  </span>
                </p>
              </>
            )}

            {onboardingStep === 2 && (
              <>
                <div
                  style={{
                    fontSize: "72px",
                    marginBottom: "24px",
                    color: colors.primary,
                  }}
                >
                  <FontAwesomeIcon icon={faHammer} />
                </div>
                <h2
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: colors.primary,
                    marginBottom: "16px",
                  }}
                >
                  Travaux (DIU)
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: colors.text,
                    lineHeight: "1.8",
                    marginBottom: "24px",
                    textAlign: "left",
                  }}
                >
                  Le dossier <strong>"Travaux"</strong> constitue le{" "}
                  <strong>DIU (Dossier d'Intervention Ultérieure)</strong>.
                  <br />
                  <br />
                  <span
                    style={{
                      backgroundColor: "rgba(180, 132, 14, 0.1)",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      display: "inline-block",
                      borderLeft: `4px solid ${colors.primary}`,
                      fontWeight: "600",
                    }}
                  >
                    Établir un Dossier d'Intervention Ultérieure est une
                    obligation légale.
                  </span>
                  <br />
                  <br />
                  Vous pouvez y classer tous les documents relatifs aux travaux
                  effectués sur votre bien : factures, garanties, notices
                  techniques, rapports d'intervention, etc.
                </p>
              </>
            )}

            {onboardingStep === 3 && (
              <>
                <div
                  style={{
                    fontSize: "72px",
                    marginBottom: "24px",
                    color: colors.primary,
                  }}
                >
                  <FontAwesomeIcon icon={faClipboard} />
                </div>
                <h2
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: colors.primary,
                    marginBottom: "16px",
                  }}
                >
                  Gestion courante
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: colors.text,
                    lineHeight: "1.8",
                    marginBottom: "24px",
                    textAlign: "left",
                  }}
                >
                  Le volet <strong>"Gestion courante"</strong> vous permet de
                  centraliser tous les documents administratifs du quotidien :
                  <br />
                  <br />
                  • Contrats d'énergie (électricité, gaz...)
                  <br />
                  • Contrats d'assurances
                  <br />
                  • Contrats de télécom (internet, téléphone...)
                  <br />
                  • Baux de location
                  <br />
                  • Charges de copropriété
                  <br />
                  • Et tout autre document de gestion courante
                  <br />
                  <br />
                  <span style={{ fontStyle: "italic", color: colors.gray }}>
                    Retrouvez facilement tous vos documents administratifs en un
                    seul endroit.
                  </span>
                </p>
              </>
            )}

            {/* Indicateurs de progression */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginTop: "32px",
                marginBottom: "24px",
              }}
            >
              {[0, 1, 2, 3].map((step) => (
                <div
                  key={step}
                  style={{
                    ...styles.onboardingDot,
                    ...(step === onboardingStep
                      ? styles.onboardingDotActive
                      : {}),
                  }}
                  onClick={() => setOnboardingStep(step)}
                ></div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              {onboardingStep > 0 && (
                <button
                  style={{
                    ...styles.button,
                    backgroundColor: "#f3f4f6",
                    color: colors.text,
                    flex: 1,
                  }}
                  onClick={() => setOnboardingStep(onboardingStep - 1)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e5e7eb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                  }}
                >
                  ← Précédent
                </button>
              )}
              <button
                style={{
                  ...styles.button,
                  ...styles.saveButton,
                  flex: 1,
                }}
                onClick={() => {
                  if (onboardingStep < 3) {
                    setOnboardingStep(onboardingStep + 1);
                  } else {
                    completeOnboarding();
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(139, 69, 19, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                }}
              >
                {onboardingStep < 3 ? (
                  <>
                    Suivant
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      rotation={180}
                      style={{ marginLeft: "8px" }}
                    />
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faRocket}
                      style={{ marginRight: "8px" }}
                    />
                    Découvrir Property Assist
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withMobilePrompt(BienDetailsPage);
